import React from 'react';
import { motion } from 'framer-motion';
import { Shield, FlaskConical, Eye, Leaf } from 'lucide-react';
import Header from '../components/Header';
import { BRAND } from '../data/brand';

const PROCESS_STEPS = [
  {
    number: '01',
    title: 'Sourcing',
    description: 'We partner exclusively with licensed cultivators who share our obsession with quality. Every grower is vetted, visited, and held to our standard.',
    icon: Leaf,
  },
  {
    number: '02',
    title: 'Testing',
    description: 'Every batch undergoes full-panel third-party lab testing. We screen for potency, terpene profiles, pesticides, heavy metals, and microbials.',
    icon: FlaskConical,
  },
  {
    number: '03',
    title: 'Review',
    description: 'Our Quality Director personally reviews every lab report. If a single metric falls below our threshold, the batch is rejected. No exceptions.',
    icon: Eye,
  },
  {
    number: '04',
    title: 'Approval',
    description: 'Only after passing all quality gates does a product receive the Secret 13 name. Less than 15% of what we evaluate makes it through.',
    icon: Shield,
  },
];

const LAB_METRICS = [
  { label: 'Batches Tested', value: '2,400+', sublabel: 'Since founding' },
  { label: 'Rejection Rate', value: '85%', sublabel: 'We accept only the best' },
  { label: 'Testing Partners', value: '3', sublabel: 'Independent labs' },
  { label: 'Contaminants Found', value: '0', sublabel: 'In approved batches' },
];

const LabPage = () => {
  return (
    <div className="min-h-screen bg-[#050505]">
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-20 px-6 md:px-12 lg:px-24">
        <div className="max-w-4xl mx-auto text-center">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="font-sans text-xs tracking-[0.3em] uppercase text-zinc-500 mb-4"
          >
            Transparency
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-zinc-50 mb-6"
            data-testid="lab-heading"
          >
            The Lab
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="font-sans text-sm text-zinc-500 max-w-lg mx-auto leading-relaxed"
          >
            Every Secret is tested, verified, and approved before it ever reaches you. Here's how.
          </motion.p>
        </div>
      </section>

      {/* Metrics */}
      <section className="py-16 px-6 md:px-12 lg:px-24 bg-[#0A0A0A]">
        <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8">
          {LAB_METRICS.map((metric, index) => (
            <motion.div
              key={metric.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: index * 0.1 }}
              className="text-center"
              data-testid={`lab-metric-${index}`}
            >
              <p className="font-serif text-3xl sm:text-4xl text-zinc-50 mb-2">{metric.value}</p>
              <p className="font-sans text-xs tracking-[0.15em] uppercase text-zinc-400 mb-1">{metric.label}</p>
              <p className="font-sans text-[10px] text-zinc-600">{metric.sublabel}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Process */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <p className="font-sans text-xs tracking-[0.3em] uppercase text-zinc-500 mb-4">Our Process</p>
            <h2 className="font-serif text-3xl sm:text-4xl font-light text-zinc-50">From Source to Secret</h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {PROCESS_STEPS.map((step, index) => {
              const Icon = step.icon;
              return (
                <motion.div
                  key={step.number}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: index * 0.15 }}
                  className="border-l border-zinc-800 pl-6"
                  data-testid={`process-step-${index}`}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <Icon className="w-4 h-4 text-zinc-600" />
                    <p className="font-sans text-xs tracking-[0.2em] text-zinc-600">{step.number}</p>
                  </div>
                  <h3 className="font-serif text-xl text-zinc-100 mb-3">{step.title}</h3>
                  <p className="font-sans text-sm text-zinc-500 leading-relaxed">{step.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Commitment */}
      <section className="py-24 px-6 md:px-12 lg:px-24 bg-[#0A0A0A]">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="w-px h-16 bg-gradient-to-b from-transparent via-zinc-700 to-transparent mx-auto mb-12" />
            <p className="font-serif text-2xl sm:text-3xl font-light text-zinc-300 leading-relaxed mb-4">
              "If we wouldn't recommend it to someone we love,
            </p>
            <p className="font-serif text-2xl sm:text-3xl font-light text-zinc-300 leading-relaxed mb-12">
              it doesn't carry our name."
            </p>
            <div className="w-12 h-px bg-zinc-700 mx-auto mb-8" />
            <p className="font-sans text-xs tracking-[0.3em] uppercase text-zinc-600">Theo Ashford, Quality Director</p>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-white/5">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-6">
          <p className="font-serif text-xl text-zinc-50">Secret 13</p>
          <p className="font-sans text-xs text-zinc-600">{BRAND.legal.copyright} {BRAND.legal.membersOnly}</p>
        </div>
      </footer>
    </div>
  );
};

export default LabPage;
